import { useTranslation } from 'react-i18next';
import CircleGraph from '../../../../../components/CircleGraph/CircleGraph';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { FlowTypeMapLabel } from '../../../enum/General';
import { DischargeType, DischargeTypeMapLabel } from '../../../enum/HydraulicData';
import { selectProject, selectResults } from '../../../selector/conversion';
import ChannelItem from './ChannelItem/ChannelItem';
import './GeneralResults.scss';
import LongitudinalSlope from './LongitudinalSlope/LongitudinalSlope';

interface IGeneralResultsProps {}

const GeneralResults = (props: IGeneralResultsProps) => {
    const { t } = useTranslation();
    const results = useAppSelector(selectResults);
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    let discharge: string | number = results.data?.generalResult.general.discharge.toFixed(2) ?? 0.0;

    if (project.data?.hydraulicData?.dischargeType == DischargeType.Manual) {
        discharge = results.data ? project.data.hydraulicData.discharge : 0;
    }

    return (
        <div id='generalResultsTables' className='m-auto d-flex justify-content-center flex-column h-85 w-70'>
            <table className='container'>
                <thead>
                    <tr>
                        <th colSpan={2}>{t('general_results_title')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className='col-6'>
                            <div>
                                <div className='d-flex justify-content-between'>
                                    <div>{t('general_results_freeboard', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</div>
                                    <div>{results.data?.generalResult.general.freeboard.toFixed(2)}</div>
                                </div>
                                <div>
                                    <i>{DischargeTypeMapLabel()[project.data!.hydraulicData!.dischargeType]}</i>
                                </div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('general_results_slope', { unit_measure: '%' })}</div>
                                <div>{results.data?.generalResult.general.longitudinalSlope.toFixed(2)}</div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('general_results_water', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{results.data?.generalResult.general.waterDepth.toFixed(2)}</div>
                            </div>
                            <div>
                                <div className='d-flex justify-content-between'>
                                    <div>{t('general_results_froude')}</div>
                                    <div>{results.data?.generalResult.general.froudeNumber.toFixed(2)}</div>
                                </div>
                                <div className='col-12'>
                                    <i>{FlowTypeMapLabel()[results.data!.generalResult.general.flowType]}</i>
                                </div>
                            </div>
                        </td>
                        <td className='col-6'>
                            <div className='d-flex justify-content-between'>
                                <div>{t('channel_item_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{discharge}</div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('channel_item_cross_area', { unit_measure: conversionLabel.m2_ft2[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{results.data?.generalResult.general.crossSectionalArea.toFixed(2)}</div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('channel_item_perimeter', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{results.data?.generalResult.general.wettedPerimeter.toFixed(2)}</div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('channel_item_radius', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{results.data?.generalResult.general.hydraulicRadius.toFixed(2)}</div>
                            </div>
                            <div className='d-flex justify-content-between'>
                                <div>{t('channel_item_velocity', { unit_measure: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number] })}</div>
                                <div>{results.data?.generalResult.general.avarageWaterVelocity.toFixed(2)}</div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table className='container'>
                <thead>
                    <tr>
                        <th className='col-3'>{t('general_results_left_overbank')}</th>
                        <th className='col-3'>{t('general_results_main_channel')}</th>
                        <th className='col-3'>{t('general_results_right_overbank')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className='col-4'>
                            <ChannelItem channel={results.data!.generalResult.leftOverbank} />
                        </td>
                        <td className='col-4'>
                            <ChannelItem channel={results.data!.generalResult.mainChannel} />
                        </td>
                        <td className='col-4'>
                            <ChannelItem channel={results.data!.generalResult.rightOverbank} />
                        </td>
                    </tr>
                    <tr>
                        <td className='col-4'>
                            <div className='chart-gr d-flex justify-content-between'>
                                {t('general_results_usage_rate')}
                                <div className='chart-wrapper'>
                                    <CircleGraph channelUsageRate={parseFloat(results.data!.generalResult.channelUsageRate.toFixed(0))} />
                                </div>
                            </div>
                        </td>
                        <td className='col-8' colSpan={2}>
                            <div className='chart-gr d-flex justify-content-between'>
                                {t('general_results_slope_title')}: {results.data!.generalResult.general.longitudinalSlope}%
                                <span className='slope-graph'>
                                    <LongitudinalSlope value={results.data!.generalResult.general.longitudinalSlope} />
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default GeneralResults;
